import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { user, loading, mustChangePassword } = useAuth();
  const location = useLocation();

  const hasRole = (role) => {
    const roles = user?.roles || [];
    if (roles.includes(role)) return true;
    if (role === 'admin') return roles.includes('super_admin');
    if (role === 'manager') return roles.includes('admin') || roles.includes('super_admin');
    return false;
  };

  const getDefaultRouteForUser = () => {
    if (hasRole('admin')) return '/admin';
    if (hasRole('manager')) return '/manager';
    return '/employee';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user must change password, redirect to login page for password change flow
  if (mustChangePassword) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return <Navigate to={getDefaultRouteForUser()} replace />;
    }
  }

  return children;
};

export const PublicRoute = ({ children }) => {
  const { user, loading, mustChangePassword } = useAuth();

  const hasRole = (role) => {
    const roles = user?.roles || [];
    if (roles.includes(role)) return true;
    if (role === 'admin') return roles.includes('super_admin');
    if (role === 'manager') return roles.includes('admin') || roles.includes('super_admin');
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  // If user must change password, let them stay on login page
  if (user && mustChangePassword) {
    return children;
  }

  if (user) {
    // Redirect to appropriate dashboard based on role hierarchy
    if (hasRole('admin')) {
      return <Navigate to="/admin" replace />;
    } else if (hasRole('manager')) {
      return <Navigate to="/manager" replace />;
    } else {
      return <Navigate to="/employee" replace />;
    }
  }

  return children;
};
